import React, { useState, useEffect, useMemo } from 'react';
import { Input } from 'components/shared/FormComponent';
import { Empty, Table, Tooltip, Popconfirm, message, Tabs } from 'components/shared/AntComponent';
import SearchIcon from 'assets/img/search.svg';
import { DeleteFilled } from 'components/shared/AntIcons';
import { Button } from 'components/shared/ButtonComponent';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getTicketInformation, saveTicket, deleteTicket } from 'services/actions/ticketActions';
import { EditIcon } from 'assets/img/custom-icons';
import dayjs from 'dayjs';
import { ResetIcon, EyeIcon } from 'assets/img/custom-icons';
import 'assets/styles/table.scss';
import 'assets/styles/account.scss';
import { resetSaveTicketState } from 'services/slices/ticketSlice';

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
    open: { label: 'Open', className: 'status-open' },
    inprogress: { label: 'In Progress', className: 'status-inprogress' },
    onhold: { label: 'On Hold', className: 'status-onhold' },
    resolved: { label: 'Resolved', className: 'status-resolved' },
    closed: { label: 'Closed', className: 'status-closed' },
};

const getStatusConfig = (status: string) => {
    const key = (status || '').toLowerCase().replace(/\s+/g, '');
    return STATUS_CONFIG[key] || { label: status || '-', className: 'status-default' };
};

const getInlineStatusStyle = (status?: string) => {
    const key = (status || '').toLowerCase().replace(/\s+/g, '');
    const styleMap: Record<string, { bg: string; text: string; dot: string; border: string }> = {
        '': { bg: '#f0f5ff', text: '#2f54eb', dot: '#2f54eb', border: '#adc6ff' },
        all: { bg: '#f0f5ff', text: '#2f54eb', dot: '#2f54eb', border: '#adc6ff' },
        open: { bg: '#d3edf9', text: '#1890ff', dot: '#1890ff', border: '#91d5ff' },
        inprogress: { bg: '#fff1d4', text: '#fa8c16', dot: '#fa8c16', border: '#ffd591' },
        onhold: { bg: '#dfdfdf', text: '#595959', dot: '#595959', border: '#d9d9d9' },
        resolved: { bg: '#e4ffc8', text: '#52c41a', dot: '#52c41a', border: '#b7eb8f' },
        closed: { bg: '#FFD6D3', text: '#f5222d', dot: '#f5222d', border: '#ff645c' },
    };
    return styleMap[key] || { bg: '#f5f5f5', text: '#595959', dot: '#595959', border: '#d9d9d9' };
};

const STATUS_FILTERS: { label: string; value: string }[] = [
    { label: 'All', value: '' },
    { label: 'Open', value: 'Open' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'On Hold', value: 'On Hold' },
    { label: 'Resolved', value: 'Resolved' },
    { label: 'Closed', value: 'Closed' },
];

const TicketingSystem: React.FC = () => {
    const navigate = useNavigate();
    const userRole = sessionStorage.getItem('role');
    const { userProfileInfo } = useSelector((state: any) => state.auth);
    const { ticketInformation, ticketInformationLoading, saveTicketSuccess, ticketDeleteSuccess, ticketDeleteError } = useSelector((state: any) => state.ticket);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchTableVal, setsearchTableval] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [allTotalCount, setAllTotalCount] = useState<number>(0);
    const [savedStatusCounts, setSavedStatusCounts] = useState<Record<string, number>>({
        open: 0,
        inprogress: 0,
        onhold: 0,
        resolved: 0,
        closed: 0,
    });

    const dispatch = useDispatch<any>();

    const filteredData = useMemo(() => {
        const rawData: any[] = ticketInformation?.ServiceRequestDetail || [];
        if (userRole === 'staff') {
            return rawData.filter((item: any) => item?.ticket_from === 2);
        }
        return rawData;
    }, [ticketInformation?.ServiceRequestDetail, userRole]);

    const data =  filteredData;
    const totalRecords = ticketInformation?.DataFinder?.totalrecords ?? 0;

    const customLocale = {
        emptyText: <Empty className="p-2" description="No tickets available" />,
    };

    useEffect(() => {
        if (statusFilter === '' && !ticketInformationLoading && ticketInformation) {
            const apiCounts = ticketInformation?.StatusCount || {};
            
            setSavedStatusCounts({
                open: apiCounts.open ?? 0,
                inprogress: apiCounts.inprogress ?? 0,
                onhold: apiCounts.onhold ?? 0,
                resolved: apiCounts.resolved ?? 0,
                closed: apiCounts.closed ?? 0,
            });
            
            setAllTotalCount(ticketInformation?.DataFinder?.totalrecords ?? 0);
        }
    }, [ticketInformation, statusFilter, ticketInformationLoading]);

    useEffect(() => {
        getTicketList(searchTableVal, pageIndex, pageSize, sortField, sortOrder, statusFilter);
    }, [pageIndex, pageSize, sortField, sortOrder, statusFilter]);

const getTicketList = (
    search = '',
    currentPage = 1,
    size = 10,
    sortByColumn = '',
    sortBy = 'desc',
    ticketStatus = ''
) => {
    const requestPayload = {
        ticketstatus: ticketStatus,
        DataFinder: {
            pagesize: size,
            currentpage: currentPage,
            sortbycolumn: sortByColumn,
            sortby: sortBy,
            searchdata: search,
        },
    };

    dispatch(getTicketInformation(requestPayload));
};

    const handleEdit = (record: any) => {
        navigate('/tickets/add-ticket', {
            state: {
                rowData: record,
            },
        });
    };

    const navigateNextScreen = (record: any) => {
        navigate('/tickets/ticket-details', {
            state: {
                rowData: record,
            },
        });
    };

    useEffect(() => {
        dispatch(resetSaveTicketState());
    }, [dispatch]);

    const handleDelete = (record: any) => {
       
        dispatch(deleteTicket(record.id) as any);
    };

    useEffect(() => {
        if (ticketDeleteSuccess) {
            message.success('Ticket deleted successfully');
            dispatch(resetSaveTicketState());            
            setPageIndex(1);
            getTicketList(searchTableVal, 1, pageSize, sortField, sortOrder, statusFilter);
        }
    }, [ticketDeleteSuccess]);

    const formatAssignedTo = (value: string) => {
    return value
        .split(' ')
        .map((word) =>
            word.toLowerCase() === 'eeg'
                ? 'EEG'
                : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ');
    };

    const columns: any[] = [
        {
            title: 'S.no',
            dataIndex: 'sno',
            key: 'sno',
            render: (id: number, record: any, index: number) => {
                if (pageIndex === 1) {
                    return index + 1;
                } else {
                    return (pageIndex - 1) * pageSize + (index + 1);
                }
            },
        },
        {
            title: 'Ticket ID',
            dataIndex: 'encoded_ticketid',
            key: 'encoded_ticketid',
            render: (encoded_ticketid: string) => <span>{encoded_ticketid || '--'}</span>,
        },
        {
            title: 'Subject',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            render: (name: string) => <span>{name || '--'}</span>,
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category: string) => (
                <span className={`${(category || '').toLowerCase()}`}>
                    {category?.toLowerCase() === 'bug'
                        ? 'Issue'
                        : category?.toLowerCase() === 'feature'
                        ? 'Clarification'
                        : (category || '-')}
                </span>
            ),
        },
        ...(userRole !== 'staff'
            ? [{
                title: 'Account Name',
                dataIndex: 'account_name',
                key: 'account_name',
                render: (account_name: string) => <span>{account_name || 'Admin'}</span>,
            }]
            : []),
        {
            title: 'Submitted Date',
            dataIndex: 'created_on',
            key: 'created_on',
            render: (created_on: any) => {
                return created_on ? dayjs(created_on).format('MM/DD/YYYY HH:mm:ss A') : '---';
            },
        },
        // {
        //     title: 'Description',
        //     dataIndex: 'description',
        //     key: 'description',
        //     render: (description: string) =>
        //         description ? (
        //             <Tooltip title={description}>
        //                 <span className="desc-cell">{description}</span>
        //             </Tooltip>
        //         ) : (
        //             <span className="desc-cell desc-empty">-</span>
        //         ),
        // },
       {
            title: 'Assigned To',
            dataIndex: 'assigned_to',
            key: 'assigned_to',
            render: (assigned_to: string) =>
                assigned_to ? (
                    <Tooltip title={formatAssignedTo(assigned_to)}>
                        <span className="desc-cell">
                            {formatAssignedTo(assigned_to)}
                        </span>
                    </Tooltip>
                ) : (
                    <span className="desc-cell desc-empty">-</span>
                ),
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            render: (priority: string) => {
                if (!priority) {
                    return <span className="desc-cell desc-empty">-</span>;
                }
                const capitalizedPriority = priority.charAt(0).toUpperCase() + priority.slice(1);
                return (
                    <Tooltip title={capitalizedPriority}>
                        <span className="desc-cell">{capitalizedPriority}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            width: 140,
            render: (status: string) => {
                const { label } = getStatusConfig(status);
                const currentStyle = getInlineStatusStyle(status);

                return (
                    <div className="status-section mx-auto d-flex justify-content-center">
                        <span
                            className="status-badge"
                            style={{
                                backgroundColor: currentStyle.bg,
                                color: currentStyle.text,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '90px',
                                height: '28px',
                                borderRadius: 0,
                                fontSize: '13px',
                                fontWeight: 700,
                                textTransform: 'capitalize',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {label}
                        </span>
                    </div>
                );
            },
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            className: 'action-column',
            render: (_id: any, record: any) => {
                return (
                    <div
                        className=" text-center d-flex justify-content-center align-items-center"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <Tooltip title="View" >
                            <span className="icon-btn icon-btn-view" onClick={() => navigateNextScreen(record)}>
                                <EyeIcon />
                            </span>
                        </Tooltip>
                        {userRole === 'admin' && (<>
                            <Tooltip title="Edit" >
                                <span className="icon-btn icon-btn-edit edit-icon" onClick={() => handleEdit(record)}>
                                    <EditIcon />
                                </span>
                            </Tooltip>
                            <Popconfirm
                                placement="topLeft"
                                title="Are you sure to delete this ticket?"
                                onConfirm={() => handleDelete(record)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Tooltip title="Delete" className="mt-0">
                                    <span className="icon-btn icon-btn-delete">
                                        <DeleteFilled className="fs-18 text-danger delete-icon" />
                                    </span>
                                </Tooltip>
                            </Popconfirm>
                        </>)}
                    </div>
                );
            },
        },
    ];

const tableChange = (pagination: any, filters: any, sorter: any) => {
    const sort = 'desc';
    const sortfield = sorter?.field || 'created_on';

    setSortField(sortfield);
    setSortOrder(sort);
    setPageIndex(pagination.current);
    setPageSize(pagination.pageSize);
};

    const handleSearch = (e: any) => {
        if (e.key === 'Enter') {
            const searchValue = e.target.value.trim();
            setsearchTableval(searchValue);
            setPageIndex(1);
            getTicketList(searchValue, 1, pageSize, sortField, sortOrder, statusFilter);
        }
    };

    const resetSearch = (e: any) => {
        if (e.key === 'Backspace' && !e.target.value) {
            setsearchTableval('');
            setPageIndex(1);
            getTicketList('', 1, pageSize, sortField, sortOrder, statusFilter);
        }
    };

    const searchbyBtn = () => {
        setPageIndex(1);
        getTicketList(searchTableVal, 1, pageSize, sortField, sortOrder, statusFilter);
    };

    const clearSearch = () => {
        setsearchTableval('');
        setPageIndex(1);
        getTicketList('', 1, pageSize, sortField, sortOrder, statusFilter);
    };

    const handleStatusFilterChange = (value: string) => {
        const val = value === 'In Progress' ? 'inprogress' : value === 'On Hold' ? 'onhold' : value === 'Resolved' ? 'resolved' : value === 'Closed' ? 'closed' : value === 'Open' ? 'open' : '';
        setStatusFilter(val);
        setPageIndex(1);
        getTicketList(searchTableVal, 1, pageSize, sortField, sortOrder, val);
    };

    const handleShowAddScreen = () => {
        navigate('/tickets/add-ticket');
    };

    const canAdd = !(userRole === 'researcher' || userProfileInfo?.data?.sub_roll === 'researcher');

    const tabItems = STATUS_FILTERS.map((f) => {
        const count = f.value === '' ? allTotalCount : (savedStatusCounts[f.label.toLowerCase().replace(/\s+/g, '')] || 0);
        
        const mappedKey = f.value === 'In Progress' ? 'inprogress' : f.value === 'On Hold' ? 'onhold' : f.value === 'Resolved' ? 'resolved' : f.value === 'Closed' ? 'closed' : f.value === 'Open' ? 'open' : '';
        const isActive = statusFilter === mappedKey;
        
        return {
            key: mappedKey,
            label: (
                <span className=" py-1 rounded-pill d-inline-block transition-all">
                    {f.label} ({count})
                </span>
            ),
            children: null
        };
    });

    return (
        <div className="p-2">
            <div className="d-flex grid-title-card align-items-center flex-wrap">
                <h5 className="my-auto">Ticketing System</h5>
                <div className="ms-auto d-flex align-items-center">
                    {canAdd && (
                        <Tooltip title="Add New Ticket">
                            <Button type="primary" onClick={handleShowAddScreen} className="ms-2">
                                Add
                            </Button>
                        </Tooltip>
                    )}
                </div>
            </div>

            <div className="custom-tabs m-0">
                <Tabs 
                    items={tabItems} 
                    activeKey={statusFilter} 
                    tabBarStyle={{ marginBottom: 0 }}
                    onChange={(key) => {
                        setStatusFilter(key);
                        setPageIndex(1);
                        // getTicketList(searchTableVal, 1, pageSize, sortField, sortOrder, key);
                    }} 
                    indicator={{ size: 0 }}
                />
            </div>

            <div className="my-2 p-3 bg-white border rounded">
                <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap">
                    <h6 className="my-auto fw-600 fs-16 text-dark">
                        List of {statusFilter ? statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) : 'All'} Tickets
                    </h6>
                    <div className="position-relative d-flex align-items-center">
                        <Input
                            prefix={<img src={SearchIcon} height="14px" onClick={searchbyBtn} className="pointer" alt="search" />}
                            onKeyDown={(e) => handleSearch(e)}
                            value={searchTableVal}
                            onChange={(e) => setsearchTableval(e.target.value.replace(/^\s+/, ''))}
                            onKeyUp={(e) => resetSearch(e)}
                            className="search-input col px-2 rounded fs-14"
                            placeholder="Search by Subject..."
                            style={{ width: '250px' }}
                        />
                        {searchTableVal && (
                            <button type="button" className="search-clear-btn position-absolute end-0 me-2 border-0 bg-transparent" onClick={clearSearch} style={{ zIndex: 5 }}>
                                ×
                            </button>
                        )}
                    </div>
                </div>

                <Table
                    className="pointer"
                    rowKey="id"
                    columns={columns}
                    dataSource={data}
                    loading={ticketInformationLoading}
                    locale={customLocale}
                    scroll={{ x: 'calc(230px + 50%)' }}
                    onChange={tableChange}
                    onRow={(record: any) => ({
                        onClick: () => navigateNextScreen(record),
                    })}
                    pagination={{
                        current: pageIndex,
                        pageSize: pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50', '100'],
                    }}
                />
            </div>
        </div>
    );
};

export default TicketingSystem;